import moment from "moment";
import {
    DataResponse,
    TimeslotResponse,
    RequestReservationResponseSuccess,
    RequestReservationResponseFailure,
    CancelReservationFailureResponse,
    CancelReservationSuccessResponse,
    Appointment,
    DayAppointmentListResponse,
    OpenAppointmentRangeResponse,
    AppointmentReservationSuccessResponse,
    AppointmentReservationErrorResponse,
    ReservationResponses,
    AppointmentCancellationResponse
} from "../types";
import { bookingApiUrl as apiUrl } from "../settings";
import { timeslotStore } from "../store/stores";
import { utoa } from "../util/functions";
import strongAuthService from "./strongAuthService";

const fetchOpenAppointmentRange = async (
    dateFrom: Date,
    dateTo: Date,
    filter: string[] = [],
): Promise<DataResponse<any>> => {
        const from = moment.utc(dateFrom).local().format("YYYY-MM-DD");
        const to = moment.utc(dateTo).local().format("YYYY-MM-DD");

        const response = await fetch(
            `${apiUrl}/appointment/calendar`,
            {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({
                    range: {
                        from,
                        to,
                    },
                    filter
                }),
            }
        );

        const responseData: OpenAppointmentRangeResponse = await response.json();

        // Sorting the string dates in this field ensures that the user will be placed
        // to the first day with available times. (Component will pick first item by default)
        // so sorting needs to be handled either in the backend or frontend.
        if (responseData && responseData.data) {
            responseData.data.sort();
        }

        const modifiedResponseData: DataResponse<any> = {
            ...responseData,
            model: 'placeholder'
        };

        return modifiedResponseData;
};


const fetchOpenAppointment = async (uuid: string): Promise<TimeslotResponse|null> => {
    const response = await fetch(
        `${apiUrl}/appointment/unreserved/${uuid}`,
        { method: "GET" }
    );

    if (response.status === 404) return null;

    const responseData = await response.json();

    return {
        ...responseData,
        tyontekija: responseData.työntekijä
    };
};


const fetchOpenAppointments = async (
    date: Date,
    filter: string[] = [],
    from: Number = 0,
    selectedExpertsUuids: string[],
    selectedPremisesUuids: string[],
    selectedServicesUuids: string[],
    limit: number = 32
): Promise<DataResponse<TimeslotResponse[]>> => {
    const formattedDate = moment.utc(date).local().format("YYYY-MM-DD");

    const response = await fetch(
        `${apiUrl}/appointment/day`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({
                date: formattedDate,
                filter,
                offset: from,
                limit
            }),
        }
    );

    const responseData: DayAppointmentListResponse = await response.json();

    const modifiedResponseData: DataResponse<TimeslotResponse[]> = {
        ...responseData,
        model: 'placeholder',
        data: responseData.data.map((appointment: Appointment) => ({
            ...appointment,
            tyontekija: appointment.työntekijä
        })),
        from: responseData.offset
    };

    await timeslotStore.updateState(
        timeslotStore.subject,
        modifiedResponseData,
        selectedExpertsUuids,
        selectedPremisesUuids,
        selectedServicesUuids,
        date
    );

    return modifiedResponseData;
};


const createReservation = async (
    uuid: string,
    ssn: string,
    firstName: string,
    lastName: string,
    phoneNumber: string,
    emailAddress: string,
    streetAddress: string,
    postalCode: string,
    localityName: string,
    dataConsent: boolean,
    marketingConsent: boolean,
    moreInformation: string,
    sendEmailConfirmation: boolean,
    sendSmsConfirmation: number | false,
    serviceVoucherCode: string | null,
    discountCode: string | null,
    bookForOther: boolean,
    reserverPhoneNumber: string | null = null,
    reserverEmail: string | null = null,
): Promise<ReservationResponses> => {
    if (!strongAuthService.authenticated())
        throw Error('User needs to authenticate before booking.');

    const extra_info = utoa(
        `${serviceVoucherCode ? `palvelusetelin numero: ${serviceVoucherCode}, ` : ""}${discountCode ? `alennuskoodi: ${discountCode}, ` : ""}${moreInformation}`
    );

    const token = strongAuthService.getToken();

    if (!token)
        throw Error('Authentication token is missing.');

    const response = await fetch(
        `${apiUrl}/appointment/booking`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                uuid,
                data: {
                    ssn,
                    first_name: utoa(firstName),
                    last_name: utoa(lastName),
                    phone_number: utoa(phoneNumber),
                    email_address: emailAddress,
                    street_address: utoa(streetAddress),
                    postal_code: utoa(postalCode),
                    locality_name: utoa(localityName),
                    consent: {
                        personal_data: dataConsent,
                        marketing_data: marketingConsent
                    },
                    extra_info,
                    // These two fields below are, as of writing, unused as both DynamicHealth and Assisdent
                    // support have been dropped and Diarium does not use these fields.
                    // Kept here for documentation purposes.
                    send_email_confirmation: sendEmailConfirmation,
                    send_sms_confirmation: sendSmsConfirmation,

                    ...(bookForOther && {
                        separate_client_and_reserver: true,
                        reserver_phone_number: reserverPhoneNumber,
                        reserver_email: reserverEmail
                    })
                }
            }),
        }
    );

    if (response.status !== 200) {
        const responseData: AppointmentReservationErrorResponse = await response.json();

        const modifiedResponseData: RequestReservationResponseFailure = {
            ...responseData,
            timeout: false
        };

        return modifiedResponseData;
    }

    const responseData: AppointmentReservationSuccessResponse = await response.json();

    const modifiedResponseData: RequestReservationResponseSuccess = {
        ...responseData,
        data: {
            cancellation_token: responseData.data["cancellation pin"]
        },
        timeout: false
    };

    return modifiedResponseData;
};


const cancelReservation = async (
    reservationUuid: string,
    pin: string
): Promise<CancelReservationSuccessResponse | CancelReservationFailureResponse> => {
    const response = await fetch(
        `${apiUrl}/appointment/booking/cancel`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({
                uuid: reservationUuid,
                data: {
                    pin
                }
            }),
        }
    );

    const responseData: AppointmentCancellationResponse = await response.json();

    if (response.status !== 200) {
        const modifiedResponseData: CancelReservationFailureResponse = {
            ...responseData,
            success: false,
            data: { // This field is currently redundant.
                error: 'placeholder'
            }
        };

        return modifiedResponseData;
    }

    const modifiedResponseData: CancelReservationSuccessResponse = {
            ...responseData,
            success: true
        };

    return modifiedResponseData;
};



export default {
    fetchOpenAppointments,
    fetchOpenAppointmentRange,
    createReservation,
    cancelReservation,
    fetchOpenAppointment
};